import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Download,
  FileText,
  CheckCircle,
  Shield,
  CloudLightning,
  Home,
  ClipboardList,
  Wrench,
  Phone,
  Mail,
  MapPin,
  User,
  ArrowRight,
  Lock,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import useSEO from "@/hooks/useSEO";
import { GUIDES, colorClasses, iconBgClasses, getGuideSubtitle } from "@/constants/guideData";
import { generateGuidePDF } from "@/constants/guideTemplates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map icon names to components
const iconMap = {
  CloudLightning,
  Shield,
  Home,
  Wrench,
  FileText
};

// Get icon component for guide
const getGuideIcon = (guideId) => {
  const icons = {
    'storm-damage-checklist': CloudLightning,
    'insurance-claim-guide': Shield,
    'roof-inspection-checklist': Home,
    'contractor-hiring-guide': Wrench,
    'minnesota-roofing-guide': FileText
  };
  return icons[guideId] || FileText;
};

// Guide Card Component
const GuideCard = ({ guide, isSelected, onSelect }) => {
  const IconComponent = getGuideIcon(guide.id);
  
  return (
    <div 
      data-testid={`guide-card-${guide.id}`}
      className={`bg-white rounded-xl border-2 ${
        isSelected ? 'border-orange shadow-lg' : 'border-gray-200 hover:border-gray-300'
      } p-6 transition-all cursor-pointer relative`}
      onClick={onSelect}
    >
      {guide.popular && (
        <div className="absolute -top-3 left-4 bg-orange text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Popular
        </div>
      )}
      
      <div className={`w-12 h-12 ${iconBgClasses[guide.color]} rounded-xl flex items-center justify-center mb-4`}>
        <IconComponent className="w-6 h-6 text-white" />
      </div>
      
      <h3 className="font-heading text-lg font-semibold text-navy mb-2">
        {guide.title}
      </h3>
      
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {guide.description}
      </p>
      
      <div className="flex items-center justify-between">
        <span className={`text-xs px-2 py-1 rounded ${colorClasses[guide.color]}`}>
          {guide.pages}
        </span>
        <span className="text-orange text-sm font-medium flex items-center gap-1">
          Download Free
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
};

// Download Form Modal Component
const DownloadModal = ({ 
  guide, 
  formData, 
  setFormData, 
  submitting, 
  downloadReady,
  onSubmit, 
  onDownload, 
  onClose 
}) => {
  if (!guide) return null;
  
  const IconComponent = getGuideIcon(guide.id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="guide-download-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${iconBgClasses[guide.color]} rounded-xl flex items-center justify-center`}>
                <IconComponent className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy">
                  {guide.title}
                </h2>
                <p className="text-sm text-gray-500">{guide.pages} • PDF Download</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="close-modal-btn"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!downloadReady ? (
            <>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Lock className="w-4 h-4" />
                  <span>Your information is secure and never shared.</span>
                </div>
                <p className="text-xs text-gray-500">
                  Enter your details below to receive instant access to your free guide.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="guide-download-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="pl-10"
                      placeholder="John Smith"
                      data-testid="guide-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10"
                      placeholder="john@example.com"
                      data-testid="guide-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10"
                      placeholder="(555) 123-4567"
                      data-testid="guide-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="pl-10"
                      placeholder="Minneapolis"
                      data-testid="guide-city-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange hover:bg-orange-600 text-white py-6 text-lg"
                  data-testid="guide-submit-btn"
                >
                  {submitting ? 'Processing...' : 'Get My Free Guide'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>

              {/* What's Included */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-navy mb-3">This guide includes:</h4>
                <ul className="space-y-2">
                  {guide.includes.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="text-center py-8" data-testid="guide-ready-section">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-navy mb-2">
                Your Guide is Ready!
              </h3>
              <p className="text-gray-600 mb-6">
                Click below to download your free {guide.title}.
                A copy has also been sent to your email.
              </p>
              
              <Button
                onClick={onDownload}
                className="bg-orange hover:bg-orange-600 text-white px-8 py-6 text-lg"
                data-testid="guide-download-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download PDF Now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Guides Page Component
export default function Guides() {
  useSEO();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/guide-downloads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        guide_id: selectedGuide?.id,
        guide_title: selectedGuide?.title
      });

      setDownloadReady(true);
      toast.success("Success! Your guide is ready to download. A copy has been sent to your email.");
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Guide request error:", err);
      }
      setDownloadReady(true);
      toast.success("Your guide is ready!");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    const subtitle = getGuideSubtitle(selectedGuide.id);
    const printContent = generateGuidePDF(selectedGuide, subtitle);
    
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
    
    toast.success(`${selectedGuide?.title} opened for download!`);
    
    setTimeout(() => {
      setSelectedGuide(null);
      setDownloadReady(false);
      setFormData({ name: "", email: "", phone: "", city: "" });
    }, 2000);
  };

  const handleCloseModal = () => {
    setSelectedGuide(null);
    setDownloadReady(false);
  };

  return (
    <div data-testid="guides-page">
      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Download className="w-4 h-4" />
              Free Resources
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-tight mb-6">
              Free Guides for Minnesota Homeowners
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed">
              Download our expert guides to help you navigate storm damage, insurance claims, 
              and roofing decisions with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Choose Your Free Guide
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each guide is packed with actionable advice specifically for Minnesota homeowners. 
              Download instantly - no strings attached.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {GUIDES.map((guide) => (
              <GuideCard
                key={guide.id}
                guide={guide}
                isSelected={selectedGuide?.id === guide.id}
                onSelect={() => {
                  setSelectedGuide(guide);
                  setDownloadReady(false);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Download Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
                Why Download Our Guides?
              </h2>
              <div className="space-y-4">
                {[
                  {
                    title: "Minnesota-Specific Information",
                    description: "Our guides are tailored to Minnesota's unique climate, building codes, and insurance landscape."
                  },
                  {
                    title: "Expert Knowledge",
                    description: "Created with input from experienced roofing professionals who work in Minnesota every day."
                  },
                  {
                    title: "Actionable Checklists",
                    description: "Step-by-step guidance you can actually use, not just generic advice."
                  },
                  {
                    title: "Completely Free",
                    description: "No hidden costs, no subscriptions. Just valuable information to help you make better decisions."
                  }
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-orange" />
                    </div>
                    <div>
                      <h3 className="font-medium text-navy mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-navy rounded-2xl p-8 text-center">
              <ClipboardList className="w-16 h-16 text-orange mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                Need Personalized Help?
              </h3>
              <p className="text-gray-400 mb-6">
                Our guides are a great starting point, but every situation is unique. 
                Connect with a trusted Minnesota roofing professional for personalized guidance.
              </p>
              <Link to="/#lead-form">
                <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-6 text-lg">
                  Get Connected
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      <DownloadModal
        guide={selectedGuide}
        formData={formData}
        setFormData={setFormData}
        submitting={submitting}
        downloadReady={downloadReady}
        onSubmit={handleSubmit}
        onDownload={handleDownload}
        onClose={handleCloseModal}
      />
    </div>
  );
}
